import { useState } from "react";
import { Lock, ArrowRight } from "lucide-react";
import { scoutLogin } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GoogleButton } from "@/components/NavAuth";
import ScoutInstall from "@/components/scout/ScoutInstall";

export const ScoutLogin = ({ onSuccess }) => {
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, logout } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await scoutLogin(passphrase);
      onSuccess();
    } catch (err) {
      setError(extractApiError(err, "Could not sign in."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="scout-login-form"
        className="w-full max-w-sm border border-slate-200 p-8"
      >
        <div className="text-xs tracking-tight text-[var(--accent)] mb-3 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5" /> ATFQ · Guest Scout
        </div>
        <h1 className="font-display text-3xl text-[#0A192F] tracking-tight">Private tool.</h1>
        <p className="mt-2 text-sm text-slate-500">Sign in with an approved Google account, or use the passphrase.</p>
        {user && !user.is_scout ? (
          <div data-testid="scout-google-denied" className="mt-6 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>{user.email}</strong> isn't on the scout allow-list.{" "}
            <button type="button" data-testid="scout-google-switch" onClick={logout} className="underline hover:text-[#0A192F]">
              Sign out and try another account
            </button>
          </div>
        ) : (
          <GoogleButton path="/scout" className="mt-6 w-full" testId="scout-google-signin" />
        )}
        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> or passphrase <span className="h-px flex-1 bg-slate-200" />
        </div>
        <input
          type="password"
          data-testid="scout-passphrase-input"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder="Passphrase"
          autoFocus
          className="w-full bg-white border border-slate-300 focus:border-[var(--accent)] px-4 py-3 text-sm outline-none"
        />
        {error && (
          <p role="alert" data-testid="scout-login-error" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="scout-login-submit"
          disabled={busy || !passphrase}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[var(--accent)] disabled:opacity-50 text-white px-5 py-3 text-sm font-medium transition-colors"
        >
          {busy ? "Checking…" : "Enter"} <ArrowRight className="w-4 h-4" />
        </button>
        <div className="mt-6 flex justify-center">
          <ScoutInstall />
        </div>
      </form>
    </div>
  );
};

export default ScoutLogin;
